<template>
  <div class="container">
    <ControlGroup>
      <DataGetter
        :url="`${url}/list/Country`"
        :params="{ CODE_INDICATOR: 'PRICE' }"
        v-slot="{ data }"
        :child-element-type="null"
        lazy
      >
        <Dropdown
          v-if="data"
          label="Country/region"
          :options="data"
          v-model="country"
        />
      </DataGetter>
    </ControlGroup>
    <EnergyProfile :url="url" :country="country" />
    <h4 class="f-title-7">Energy prices by sector</h4>
    <ControlGroup>
      <DataGetter
        :url="`${url}/list/CODE_SECTOR`"
        :params="{ Country: country, CODE_INDICATOR: 'PRICE' }"
        @fetched="setSector"
      />
      <ButtonGroup :options="sectors" v-model="sector" title="Sector" />
      <DataGetter
        :url="`${url}/list/CODE_UNIT`"
        :params="{
          Country: country,
          CODE_INDICATOR: 'PRICE',
          CODE_SECTOR: sector,
        }"
        :initialData="[{ label: 'USD', value: 'USDCUR' }]"
        v-slot="{ data }"
        :child-element-type="null"
        lazy
      >
        <RadioGroup title="Show price in" :options="data" v-model="unit" />
      </DataGetter>
      <DataGetter
        :url="`${url}/list/CODE_YEAR`"
        :params="{
          CODE_INDICATOR: 'PRICE',
          CODE_SECTOR: sector,
          Country: country,
          CODE_UNIT: unit,
        }"
        :initialData="[year]"
        v-slot="{ data }"
        :child-element-type="null"
        @fetched="
          (data) => {
            if (alwaysMostRecent) year = data[data.length - 1];
          }
        "
        lazy
      >
        <Dropdown
          label="Year"
          :options="[...data].reverse()"
          v-model="year"
          small
          reset-on-invalid
        />
      </DataGetter>
    </ControlGroup>
    <ControlGroup right>
      <Toggle
        label="Always show most recent"
        small
        v-model="alwaysMostRecent"
      />
    </ControlGroup>
    <DataGetter
      :url="url"
      :params="{
        Country: country,
        CODE_INDICATOR: indicator,
        CODE_SECTOR: sector,
        CODE_UNIT: unit,
      }"
      @fetched="setIndicators"
    />
    <IndicatorBox
      :key="`Indicators${sector}${year}`"
      :title="`${sectorName} energy prices, ${country}`"
      :indicators="indicators"
      :items-per-row="showPriceChange ? 2 : 3"
      compact
    />
    <ControlGroup>
      <DataGetter
        :url="`${url}/list/CODE_PRODUCT`"
        :params="{
          Country: country,
          CODE_INDICATOR: 'PRICE',
          CODE_SECTOR: sector,
          CODE_YEAR: year,
        }"
        v-slot="{ data }"
        @fetched="setProducts"
        :child-element-type="null"
      >
        <Dropdown
          v-if="data"
          label="Show product"
          :options="data"
          v-model="product"
          reset-on-invalid
        />
      </DataGetter>
    </ControlGroup>
    <div class="m-block m-block--charts m-block--charts-half">
      <PriceTimeSeries
        :url="url"
        :baseCountry="country"
        :sector="sector"
        :product="product"
        :topicName="product ? productName : sectorName"
        :unit="unit"
        half
      />
      <CountryRanking
        :url="url"
        :country="country"
        :product="product"
        :sector="sector"
        :year="year"
        :unit="unit"
      />
    </div>
  </div>
</template>

<script>
import {
  DataGetter,
  ControlGroup,
  ButtonGroup,
  RadioGroup,
  Dropdown,
  Toggle,
  IndicatorBox,
} from "@international-energy-agency/vue-ui";
import countryMixin from "./countryMixin";
import PriceTimeSeries from "./PriceTimeSeries.vue";
import EnergyProfile from "./EnergyProfile.vue";
import CountryRanking from "./CountryRanking.vue";
const allSectors = [
  { label: "Transport", value: "TRANS" },
  { label: "Residential", value: "RESID" },
  { label: "Industry", value: "IND" },
  { label: "Electricity generation", value: "ELGEN" },
];
export default {
  props: {
    url: {
      type: String,
      default: "https://api.iea.org/prices",
      required: false,
    },
  },
  components: {
    DataGetter,
    ControlGroup,
    ButtonGroup,
    RadioGroup,
    Dropdown,
    Toggle,
    IndicatorBox,
    PriceTimeSeries,
    EnergyProfile,
    CountryRanking,
  },
  mixins: [countryMixin],
  data() {
    return {
      sector: "TRANS",
      unit: "USDCUR",
      sectors: allSectors,
      product: "GASOLINE",
      products: [{ value: "GASOLINE", label: "Motor gasoline" }],
      indicator: "PRICE",
      indicatorData: [],
      year: "2023",
      showPriceChange: false,
      lockPriceChange: true, // only show price change for year - 1
      alwaysMostRecent: true,
    };
  },
  computed: {
    sectorName() {
      const { sector } = this;
      return this.sectors.find((s) => s.value === sector)?.label;
    },
    productName() {
      const { product } = this;
      return this.products.find((p) => p.value === product)?.label || "";
    },
    indicators() {
      const { indicatorData, showPriceChange, lockPriceChange, year } = this;
      const lastYear = indicatorData.reduce(
        (lastYear, row) =>
          parseInt(row.CODE_YEAR) > lastYear &&
          parseInt(row.CODE_YEAR) <= parseInt(year)
            ? parseInt(row.CODE_YEAR)
            : lastYear,
        1990
      );
      const indicators = [];
      indicatorData
        .filter((row) => parseInt(row.CODE_YEAR) === lastYear)
        .forEach((row) => {
          indicators.push({
            title: `${row.Product}, ${row.CODE_YEAR}`,
            number: row.Value,
            units: row.Unit,
          });
          const previousYear = lockPriceChange
            ? indicatorData.find(
                (c) =>
                  c.Product === row.Product &&
                  parseInt(c.CODE_YEAR) === lastYear - 1
              )
            : indicatorData.reduce(
                (previous, c) =>
                  c.Product === row.Product &&
                  (!previous ||
                    parseInt(c.CODE_YEAR) > parseInt(previous.CODE_YEAR)) &&
                  parseInt(c.CODE_YEAR) < lastYear
                    ? c
                    : previous,
                null
              );
          if (showPriceChange) {
            indicators.push({
              title: "Price change",
              key: `priceChange_${row.Product}`,
              number: previousYear
                ? parseFloat(
                    (
                      ((row.Value - previousYear.Value) / previousYear.Value) *
                      100
                    ).toFixed(1)
                  )
                : null,
              arrow: true,
              units: "%",
              description: `from ${
                previousYear?.CODE_YEAR || lastYear - 1
              } price`,
            });
          }
        });
      return indicators;
    },
  },
  methods: {
    setSector(sectors) {
      this.sectors = allSectors.map((sector) => ({
        ...sector,
        disabled:
          sectors.findIndex((option) => option.value === sector.value) === -1,
      }));
      const { sector } = this;
      if (sectors.findIndex((option) => option.value === sector) === -1) {
        this.sector = sectors[0].value;
      }
    },
    setProducts(products) {
      this.products = products;
    },
    setIndicators(data) {
      this.indicatorData = data;
    },
  },
};
</script>

<style scoped>
.flex-chart-container {
  display: flex;
  align-items: space-between;
}
.flex-chart-container .m-chart-block {
  flex: 1;
}
</style>